/**
 * Utility functions for message formatting and processing
 */

import { AGENT_TYPES, AGENT_DISPLAY_CONFIG, SENDER_AGENT } from '../constants';
import { firstImageUrl, isBrowserScreenshotTool } from '../../../../utils/toolImages';

/**
 * Format a timestamp for display
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Get the display name for an agent
 * @param {string} sender - The sender type
 * @param {string} agentType - The specific agent type
 * @returns {string} - Display name for the agent
 */
export const getAgentName = (sender, agentType) => {
  if (sender === 'system') return 'System';
  if (sender === 'user') return 'You';
  
  // If sender is generic "agent", use the agentType
  const effectiveSender = sender === SENDER_AGENT ? agentType : sender;
  
  const config = AGENT_DISPLAY_CONFIG[effectiveSender];
  if (config) return config.text;
  
  return 'Kolega';
};

/**
 * Get the agent display configuration
 * @param {string} sender - The sender type
 * @param {string} agentType - The specific agent type
 * @returns {Object} - Display configuration with icon, text, and colorClass
 */
export const getAgentDisplay = (sender, agentType) => {
  const effectiveSender = sender === SENDER_AGENT ? agentType : sender;
  
  const config = AGENT_DISPLAY_CONFIG[effectiveSender];
  if (config) {
    return config;
  }
  
  // Default configuration
  return {
    icon: 'MessageSquare',
    text: effectiveSender,
    colorClass: 'text-semantic-warning'
  };
};

/**
 * Generate a unique message ID
 * @param {number} index - Message index
 * @param {Object} message - Message object
 * @returns {string} - Unique message ID
 */
export const generateMessageId = (index, message) => {
  const uuid = message.uuid || '';
  const timestamp = new Date(message.lastUpdated || 0).getTime();
  return `${index}-${uuid}-${timestamp}`;
};

/**
 * Check if a message is currently streaming
 * @param {Object} message - Message object
 * @param {string} agentStatus - Current agent status
 * @returns {boolean} - Whether the message is streaming
 */
export const isMessageStreaming = (message, agentStatus) => {
  return agentStatus === 'generating' && (
    message.isStreaming === true || 
    message.is_streaming === true || 
    (typeof message.isStreaming === 'undefined' && 
     typeof message.is_streaming !== 'undefined' && 
     message.is_streaming === true)
  );
};

/**
 * Get message style classes based on sender and type
 * @param {string} sender - Message sender
 * @param {boolean} isSubAgent - Whether this is a sub-agent message
 * @param {string} agentType - Specific agent type
 * @returns {string} - CSS class string
 */
export const getMessageStyle = (sender, isSubAgent, agentType) => {
  if (sender === 'user') {
    return 'bg-brand-action text-brand-action-text text-sm shadow-sm';
  }
  
  if (sender === 'system') {
    return 'bg-white dark:bg-neutral-800 text-ui-text-primary text-sm shadow-sm';
  }
  
  // For sub-agent messages, use a special gradient background
  if (isSubAgent) {
    return 'bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 text-ui-text-primary text-sm border-l-4 border-purple-500 dark:border-purple-400 shadow-sm';
  }
  
  // If sender is generic "agent", use the agentType
  const effectiveSender = sender === SENDER_AGENT ? agentType : sender;
  
  if ([AGENT_TYPES.CODING, AGENT_TYPES.INVESTIGATION, AGENT_TYPES.CODER].includes(effectiveSender)) {
    return 'bg-white dark:bg-neutral-800 text-ui-text-primary text-sm border-l-4 border-accent-purple dark:border-accent-light-purple shadow-sm';
  }
  
  if (effectiveSender === AGENT_TYPES.BROWSER) {
    return 'bg-white dark:bg-neutral-800 text-ui-text-primary text-sm border-l-4 border-semantic-info shadow-sm';
  }
  
  return 'bg-white dark:bg-neutral-800 text-ui-text-primary text-sm border-l-4 border-neutral-300 dark:border-neutral-700 shadow-sm';
};

/**
 * Process messages to identify and group tool calls with their results
 * Only the last message can be pending - all others without results are failed
 * @param {Array} messages - Array of all messages
 * @returns {Array} - Processed messages with tool call status
 */
export const processToolCallMessages = (messages) => {
  if (!messages || messages.length === 0) return messages;
  
  // Build a map of tool_call_id to all related messages
  const toolCallMap = new Map();
  const processedMessages = [];
  const lastMessageIndex = messages.length - 1;
  
  // First pass: collect all tool messages by tool_call_id
  messages.forEach((message, index) => {
    const toolCallId = message.tool_call_id;
    const messageType = message.message_type;
    
    // Check if this is a tool-related message
    if (toolCallId && (messageType === 'tool' || messageType === 'tool_call' || messageType === 'tool_result' || messageType === 'tool_error')) {
      if (!toolCallMap.has(toolCallId)) {
        toolCallMap.set(toolCallId, {
          call: null,
          result: null,
          callIndex: -1,
          resultIndex: -1
        });
      }
      
      const entry = toolCallMap.get(toolCallId);
      
      // Distinguish between tool calls and results based on message_type
      if (messageType === 'tool_call' || (messageType === 'tool' && message.is_tool_call)) {
        // This is a tool call
        entry.call = message;
        entry.callIndex = index;
      } else if (messageType === 'tool_result' || messageType === 'tool_error' || messageType === 'tool') {
        // This is a tool result/error
        entry.result = message;
        entry.resultIndex = index;
      }
    }
  });
  
  // Second pass: create consolidated messages with status
  const processedToolCallIds = new Set();
  
  messages.forEach((message, index) => {
    const toolCallId = message.tool_call_id;
    const messageType = message.message_type;
    
    // Check if this is a tool-related message
    if (toolCallId && (messageType === 'tool' || messageType === 'tool_call' || messageType === 'tool_result' || messageType === 'tool_error')) {
      // Only process each tool_call_id once
      if (!processedToolCallIds.has(toolCallId)) {
        processedToolCallIds.add(toolCallId);
        
        const entry = toolCallMap.get(toolCallId);
        let status = 'pending';
        let error = null;
        let resultContent = null;
        
        // Determine status based on what we have
        if (entry.result) {
          // We have a result - check the message type
          resultContent = entry.result.content;
          const resultType = entry.result.message_type;
          
          if (resultType === 'tool_error') {
            status = 'error';
            error = resultContent || 'Tool execution failed';
          } else if (resultType === 'tool_result') {
            status = 'success';
          } else if (resultType === 'tool') {
            // Legacy format - check content for errors
            const toolDescription = entry.result.tool_description || '';
            const isError = entry.result.is_error || 
                           toolDescription.includes('ERROR') || 
                           (typeof resultContent === 'string' && resultContent.toLowerCase().includes('error'));
            
            status = isError ? 'error' : 'success';
            if (isError) {
              error = typeof resultContent === 'string' ? resultContent : 'Tool execution failed';
            }
          }
        } else if (entry.call && entry.callIndex !== lastMessageIndex) {
          // Check if there are any messages after this tool call that aren't:
          // 1. Sub-agent messages (part of this tool's execution)
          // 2. The result for THIS specific tool call
          let hasInvalidMessagesAfter = false;
          
          for (let i = entry.callIndex + 1; i <= lastMessageIndex; i++) {
            const msg = messages[i];
            
            // Skip sub-agent messages - they're part of the tool execution
            if (msg.isSubAgent) {
              continue;
            }
            
            // Skip if this is the result for THIS tool call
            if (msg.tool_call_id === toolCallId && 
                (msg.message_type === 'tool_result' || 
                 msg.message_type === 'tool_error' || 
                 msg.message_type === 'tool')) {
              continue;
            }
            
            // Any other message means the tool call failed
            hasInvalidMessagesAfter = true;
            break;
          }
          
          if (hasInvalidMessagesAfter) {
            // Tool call without result and has other messages after - it failed
            status = 'failed';
            error = 'Tool call did not complete';
          }
          // else: Tool call is still pending (only has sub-agent messages or its own result)
        }
        // else: Tool call without result but is the last message - remains 'pending'
        
        // Use the call message as base if available, otherwise use result
        const baseMessage = entry.call || entry.result;
        const toolName =
          entry.call?.tool_description ||
          entry.call?.tool_name ||
          entry.result?.tool_description ||
          entry.result?.tool_name ||
          'Unknown Tool';
        const screenshotUrl = isBrowserScreenshotTool(toolName)
          ? firstImageUrl(entry.result?.images) || firstImageUrl(entry.result?.content)
          : null;

        if (screenshotUrl) {
          resultContent = screenshotUrl;
        }
        
        processedMessages.push({
          ...baseMessage,
          isToolCall: true,
          toolCallId: toolCallId,
          toolStatus: {
            // If streaming, override status to 'streaming' regardless of what was calculated
            status: baseMessage.toolStatus?.isStreaming ? 'streaming' : status,
            error,
            toolName,
            resultContent,
            // Preserve streaming fields from the original message
            streamingContent: baseMessage.toolStatus?.streamingContent,
            isStreaming: baseMessage.toolStatus?.isStreaming
          }
        });
      }
    } else if (messageType !== 'tool_call' && messageType !== 'tool_result' && messageType !== 'tool_error') {
      // Non-tool message, include as-is
      processedMessages.push(message);
    }
  });
  
  return processedMessages;
};
